<script setup>
import { computed, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { Icon } from '@iconify/vue'
import VoteController from '@/actions/App/Http/Controllers/VoteController'

// Pages where the alert would be redundant or out of place
const HIDDEN_ON = ['Vote', 'Screen/Screen', 'Reimbursements/Print']

const page = usePage()

// Null when the user can't vote; otherwise the open votes they haven't cast yet
const pendingVotes = computed(() => page.props.pendingVotes ?? null)
const show = computed(() => pendingVotes.value?.length > 0 && !HIDDEN_ON.includes(page.component))

function refresh () {
  router.reload({ only: ['pendingVotes'] })
}

// Only voters may join the Votes channel, so subscribe once we know the user can vote
let listening = false
watch(() => pendingVotes.value !== null, canVote => {
  if (!canVote || listening || !window.Echo) return
  listening = true
  window.Echo.private('Votes').listenForWhisper('refreshVotes', refresh)
  window.Echo.connector.pusher.connection.bind('connected', refresh)
}, { immediate: true })
</script>

<template>
  <!-- Live region stays mounted so screen readers announce the card when it appears -->
  <div
    aria-live="assertive"
    aria-atomic="true"
    class="fixed top-[calc(var(--spacing-nav)+1rem)] right-4 left-4 sm:left-auto sm:w-96 z-1500 pointer-events-none"
  >
    <div v-if="show" class="open-vote-card pointer-events-auto flex flex-col gap-4 bg-yellow text-green-pine p-site shadow-[.5rem_.5rem_0_0_var(--color-green-pine)]">
      <div class="flex items-start gap-3">
        <Icon icon="ri:hand" class="shrink-0 text-lg" aria-hidden="true" />
        <div class="flex flex-col gap-3">
          <p class="font-headline uppercase text-md leading-none">A vote is open</p>
          <p class="font-semibold">{{ pendingVotes.map(vote => vote.name).join(', ') }}</p>
          <InputButton :href="VoteController.vote.url()" variant="white" icon-end="ri:arrow-right-line" class="self-start">
            Cast your vote
          </InputButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.open-vote-card {
  animation: shake 4s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .open-vote-card {
    animation: none;
  }
}

/* Quick shake, then rest for the rest of the cycle */
@keyframes shake {
  0%, 15%, 100% { translate: 0; }
  2%, 6%, 10% { translate: -6px; }
  4%, 8%, 12% { translate: 6px; }
}
</style>
